import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import ReCAPTCHA from 'react-google-recaptcha';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CountryIso2, CountrySelector } from 'react-international-phone';
import { phone } from 'phone';
import { REGISTER } from './mutations';
import AuthHeaderLayout from './components/AuthHeaderLayout';
import VerifyOTP from './components/VerifyOTP';
import GoogleButton, { GoogleProfile } from './components/GoogleButton';
import FacebookButton, { FacebookProfile } from './components/FacebookButton';
import AppleButton from './components/AppleButton';
import Account_Message from './components/Messages';
import { ExclamationIcon } from '../Global/icons';
import LabelTextInput from '../Global/LabelTextInput';
import InvisibleRecaptcha from '../Global/InvisibleRecaptcha';
import ConfirmationScreen from '../Global/ConfirmationScreen';
import TabSwitch from '../TabSwitch';
import { TabSwitchDefaultTabs } from '../TabSwitch/types';
import { LOGIN_VIA, PROFILE_SETUP_PATH } from '../../helpers/config';
import { mapUserZone } from '../../helpers/getUserTimeZone';
import { validateEmail } from '../../helpers/validators';
import { parseTryCatchError } from '../../helpers/parseTryCatchError';
import { defaultCountries, parseCountry } from 'react-international-phone';
import { allowedCountries } from '../../settings.json';
import { onlyNumbers, setToken } from '../../helpers';

import './react-international-phone.css';
import './styles.css';

const faSpinnerIcon = faSpinner as IconProp;

interface Proptypes {
  onSubmit: (
    email: string,
    password: string,
    onError: (message: string) => void,
    onSuccess: Function
  ) => void;
  handleRedirection?: (onboard_status?: boolean) => void;
}

interface SocialData {
  social_id: string;
  name?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  email_verified: boolean;
  login_via?: number;
}

const Login = (props: Proptypes) => {
  const { handleRedirection } = props;
  const [googleRegisterMutation] = useMutation(REGISTER());
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyOTP, setVerifyOTP] = useState(false);
  const [activeTab, setActiveTab] = useState<TabSwitchDefaultTabs['tabs']>(
    'Email'
  );
  const [countryCode, setCountryCode] = useState({
    dialCode: '1',
    country: 'us' as CountryIso2,
  });
  // Phone Number and Validation
  const [phoneNumber, setPhoneNumber] = useState('');
  const reCaptchaRef = useRef<ReCAPTCHA>(null);

  // FOR COUNTRY AND TIMEZONE
  const ct = require('countries-and-timezones');
  const [timezone, setTimeZone] = useState('');
  const [country, setCountry] = useState('');

  // For registration confirmation screen
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [socialFormData, setSocialFormData] = useState<
    SocialData | undefined
  >();
  const countries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return allowedCountries.includes(iso2);
  });
  useEffect(() => {
    const getTimezone = async () => {
      let timezoneFromIntl = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let userTimeZone = await mapUserZone(timezoneFromIntl);
      let userCountry = await ct.getCountryForTimezone(userTimeZone);
      setTimeZone(userTimeZone);
      setCountry(await mapUserZone(userCountry?.name, 'country'));
    };
    getTimezone();
  }, []);

  // Handle Error
  const handleError = (err) => {
    // IF CONDITION TO CHECK FOR THE VALIDATION ERROR CAUSED DUE TO USERNAME BEING ALREADY TAKEN
    if (
      err &&
      err.graphQLErrors &&
      err.graphQLErrors[0]?.extensions?.validation
    ) {
      const { name, email: errorEmail } =
        err.graphQLErrors[0].extensions.validation || {};

      let message = 'Some error occurred';
      if (Array.isArray(errorEmail)) {
        message = errorEmail[0];
      } else if (Array.isArray(name)) {
        message = name[0];
      }
      setErrorMessage(message || parseTryCatchError(err));
    } else {
      setErrorMessage(parseTryCatchError(err));
    }
    setLoading(false);
  };

  const registerGoogle = (data: SocialData) => {
    setLoading(true);
    googleRegisterMutation({
      variables: {
        email: data.email,
        social_id: data.social_id,
        user_name: data.name,
        timezone: timezone,
        email_verified: data.email_verified,
        country: country,
        login_via: data.login_via ?? LOGIN_VIA.google,
        register_from: 1,
      },
    })
      .then(async (val) => {
        const registerResponse = val.data.register;
        var responseData = registerResponse.data;
        if (responseData.access_token) {
          await setToken(responseData.access_token, responseData.user?.id);
          if (responseData.user?.profile?.onboard_status) {
            window.location.replace(PROFILE_SETUP_PATH);
          } else {
            window.location.replace('/');
          }
        } else {
          let errorMessage = 'Something went wrong. Please try again later.';
          if (registerResponse?.messages?.error?.length > 0)
            errorMessage = registerResponse.messages.error[0];
          setErrorMessage(errorMessage);
          setLoading(false);
        }
      })
      .catch((err) => {
        handleError(err);
      });
  };

  const registerRedirect = (profileObj: GoogleProfile | FacebookProfile) => {
    const formData = {
      social_id:
        // @ts-ignore
        profileObj.googleId || profileObj.facebookId || profileObj.userID,
      email: profileObj.email?.length > 0 ? profileObj.email : '',
      email_verified: profileObj.email ? true : false,
      login_via: profileObj.login_via ?? LOGIN_VIA.google,
    };
    setSocialFormData(formData);
    setShowConfirmation(true);
  };

  // Uncommenting this will be utilized for getting back the email password login functionality
  /*
  const onLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    const isHuman = await ValidateHuman(reCaptchaRef.current);
    if (!isHuman) return;
    setIsProcessing(true);
    onSubmit(
      email,
      password,
      (message) => {
        setErrorMessage(message);
        setIsProcessing(false);
      },
      () => {}
    );
  };
  */
  // Return Form Fields based on Active Tab
  const returnFormFields = () => {
    if (activeTab === 'Email') {
      return (
        <>
          <div className="form-group">
            <LabelTextInput
              placeholder="Enter your email"
              inputType={'email'}
              inputName={'email'}
              value={email}
              label="Email"
              className={`input-field ${errorMessage ? 'is-invalid' : ''}`}
              onChange={(newEmail) => {
                setEmail(newEmail);
              }}
              disabled={isProcessing}
              required
              showLabelOnFocus
            />
          </div>
          {/* Removed password field for login, might be used later  */}
          {/* <div className="form-group mt-10">
            <LabelTextInput
              placeholder="Password"
              inputType={'password'}
              inputName={'password'}
              value={password}
              label="Password"
              className={`input-field ${errorMessage ? 'is-invalid' : ''}`}
              onChange={(newPass) => {
                setPassword(newPass);
              }}
              disabled={isProcessing}
              required
              showLabelOnFocus
              togglePass
            />
          </div> */}
        </>
      );
    }
    return (
      <div className="form-group auth__phone">
        <CountrySelector
          selectedCountry={countryCode.country}
          className="auth__phone-country"
          onSelect={({ dialCode, iso2: country }) => {
            setCountryCode({ dialCode, country });
          }}
          countries={countries}
        />
        <div className="auth__phone-country__dialcode">
          +{countryCode.dialCode}
        </div>
        <LabelTextInput
          placeholder="(201) 555-0123"
          inputType={'text'}
          inputName={'phoneNumber'}
          value={phoneNumber}
          className={`auth__phone-input ${errorMessage ? 'is-invalid' : ''}`}
          onChange={(val) => {
            setPhoneNumber(
              onlyNumbers({
                value: val,
                oldValue: phoneNumber,
                max: 13,
                compareLength: true,
              })
            );
          }}
          required
        />
      </div>
    );
  };

  // Check IF Get Code button needs to be disabled
  const isOTPDisabled = () => {
    return (
      isProcessing ||
      ((email.length <= 0 || !validateEmail(email)) && activeTab === 'Email') ||
      (!phone(phoneNumber, {
        country: countryCode.country,
      }).isValid &&
        activeTab === 'SMS')
    );
  };

  // Updating verifyOTP to show OTP Screen
  const updateVerifyOTP = () => {
    if (activeTab === 'Email') {
      if (validateEmail(email)) {
        setVerifyOTP(true);
        setErrorMessage('');
      } else {
        setErrorMessage('The email must be a valid email address.');
      }
    } else {
      if (
        phone(phoneNumber, {
          country: countryCode.country,
        }).isValid
      ) {
        setVerifyOTP(true);
        setErrorMessage('');
      } else {
        setErrorMessage('The number must be a valid phone number.');
      }
    }
  };

  const tabSwitchHandler = (tab: string) => {
    setActiveTab(tab as TabSwitchDefaultTabs['tabs']);
    setErrorMessage(null);
  };

  // Return VerifyOTP Screen or Login Screen
  const returnWrapper = () => {
    if (verifyOTP) {
      return (
        <VerifyOTP
          isLogin
          sentTo={
            activeTab === 'Email'
              ? email
              : `+${countryCode.dialCode} ${phoneNumber}`
          }
          isMobileNumber={activeTab !== 'Email'}
          countryCode={countryCode.dialCode}
          verifyFor="login"
          isProcesssing={isProcessing}
          setVerifyOTP={setVerifyOTP}
          setIsProcessing={setIsProcessing}
          onSuccess={() => {}}
          error={errorMessage}
          setError={setErrorMessage}
          confirmRegister
        />
      );
    }
    return (
      <form>
        <div className="auth-wrapper">
          {/* <img src="/dashboard/img/login-squirel.svg" alt=" squirel login" /> */}
          <svg
            width="55"
            height="21"
            viewBox="0 0 55 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M54.0287 4.54861C53.9943 4.51153 53.9571 4.47881 53.9302 4.45871C53.7132 4.27222 53.4362 4.18496 53.1916 4.04196C52.7588 3.79181 52.5769 3.4295 52.1662 3.14456C51.897 2.95854 51.4028 2.80053 51.0896 2.71767C50.4437 2.54883 49.839 2.43049 49.1729 2.40427C48.947 2.39514 48.5226 2.38197 48.1179 2.42408C47.9906 2.2884 47.8557 2.16932 47.7233 2.06091C47.7284 2.06722 47.7328 2.06998 47.7407 2.07194C46.7138 1.01813 45.1523 0.341573 45.1523 0.341573C45.1523 0.341573 45.1792 0.611702 45.2439 1.00027C45.1171 0.983878 45.0271 0.981648 45.0271 0.981648C45.0271 0.981648 45.2264 1.6386 45.5579 2.45255C45.6624 2.80234 45.7857 3.13671 45.9374 3.39756L45.9358 3.39045C46.0255 3.65772 46.1539 3.93125 46.3309 4.20509C46.3309 4.20509 46.3309 4.20509 46.3317 4.20865C46.3333 4.21575 46.3285 4.22801 46.3266 4.23591C46.3215 4.26317 46.32 4.28964 46.3149 4.3169C46.3165 4.32401 46.3145 4.33191 46.3126 4.33981C46.3083 4.37063 46.3048 4.405 46.3005 4.43581C46.3005 4.43581 46.3005 4.43581 46.3013 4.43937C46.2951 4.51166 46.2924 4.58316 46.2898 4.65465C46.2898 4.65465 46.2898 4.65465 46.2906 4.65821C46.2907 4.69178 46.2907 4.72536 46.2908 4.75893C46.2916 4.76248 46.2932 4.76959 46.294 4.77314C46.2932 4.80316 46.2968 4.83594 46.2961 4.86596C46.2969 4.86952 46.2985 4.87662 46.2993 4.88018C46.3029 4.91295 46.3022 4.94297 46.3058 4.97575C46.3058 4.97575 46.3066 4.97931 46.3074 4.98286C46.2253 5.01617 46.1397 5.05027 46.0568 5.08002C39.1817 7.41087 32.2786 4.73609 28.6107 8.22954C28.5764 8.25961 28.5465 8.29244 28.5173 8.32882C28.5173 8.32882 28.3195 8.5448 28.0797 8.92317C28.0505 8.92598 28.0213 8.92879 27.9921 8.9316C27.9708 8.93638 27.9478 8.93404 27.9265 8.93881C27.9017 8.94438 27.8752 8.94284 27.8496 8.94486C27.8247 8.95043 27.7982 8.94889 27.7726 8.9509C27.7513 8.95568 27.7319 8.95255 27.7098 8.95376C27.6806 8.95658 27.6549 8.95859 27.6285 8.95705L27.6214 8.95864C27.555 8.9623 27.4852 8.96675 27.4188 8.97041C21.3483 9.21408 17.1372 4.5183 10.689 3.0254C8.43039 2.5012 5.44602 3.05014 3.87336 4.15618C2.3007 5.26222 1.23913 6.70163 0.835194 8.72894C2.26724 6.91175 8.16531 6.49771 10.4059 7.10804C16.7242 8.82036 20.1809 12.4125 27.3553 9.68623C27.3754 9.87579 27.4215 10.0483 27.4889 10.1825L27.5007 10.1687C27.4661 10.2809 27.4332 10.4002 27.4081 10.5215C26.6712 13.8102 25.5001 13.1618 25.0905 13.2984L25.0834 13.3C25.0763 13.3015 25.0692 13.3031 25.0629 13.3083C22.9664 14.207 21.346 16.2492 21.0957 16.7307C20.6176 17.6439 20.2188 19.4946 20.3656 20.0999C20.3863 20.1923 20.5573 20.2062 20.6753 20.2171C21.1105 20.2615 21.3204 19.9495 21.5091 19.7095C22.2202 17.9044 22.672 16.5568 24.6483 15.1215C23.6598 16.0706 23.0029 17.0686 22.8653 17.3868C22.45 18.3307 22.1796 20.2049 22.369 20.8007C22.3968 20.8915 22.5654 20.8948 22.6854 20.8977C23.1214 20.9121 23.3095 20.5863 23.4844 20.3344C24.1134 18.3463 24.4817 16.9091 26.7486 15.1886C27.2902 15.3919 27.9859 15.5832 28.8355 15.5795C29.0086 15.6191 29.195 15.652 29.3912 15.679C29.3912 15.679 29.3948 15.6782 29.3984 15.6774C29.4177 15.6805 29.4327 15.6809 29.4521 15.684C29.4592 15.6824 29.4635 15.6852 29.4714 15.6872C29.4872 15.6911 29.5058 15.6907 29.5208 15.691C29.5279 15.6894 29.5358 15.6914 29.5472 15.6926C29.5622 15.6929 29.578 15.6969 29.593 15.6972C29.6037 15.6948 29.6124 15.7004 29.623 15.698C29.6381 15.6983 29.6531 15.6987 29.6689 15.7026C29.6795 15.7003 29.6918 15.705 29.7032 15.7061C29.7147 15.7073 29.7297 15.7077 29.7411 15.7089C29.764 15.7112 29.7826 15.7108 29.8055 15.7131C29.8126 15.7115 29.8205 15.7135 29.8284 15.7154C29.8513 15.7178 29.8734 15.7165 29.8963 15.7189C29.9034 15.7173 29.9113 15.7193 29.9192 15.7212C29.9378 15.7208 29.9571 15.7239 29.9757 15.7235C29.9828 15.7219 29.9907 15.7239 30.0021 15.725C30.0207 15.7246 30.04 15.7277 30.0622 15.7265C30.0693 15.7249 30.0764 15.7233 30.0843 15.7253C30.1064 15.7241 30.1257 15.7272 30.1478 15.726C30.1549 15.7244 30.1585 15.7236 30.1664 15.7256C30.1885 15.7244 30.2149 15.7259 30.2371 15.7247C30.2406 15.7239 30.2442 15.7231 30.2477 15.7223C30.7223 15.7093 31.2417 15.6303 31.8068 15.4552C32.7342 15.1654 33.713 14.2894 34.4972 13.4271C36.9791 13.4161 40.6541 12.6864 43.3774 11.8377C43.42 11.8281 43.4575 11.8123 43.4993 11.7992C43.51 11.7968 43.5206 11.7944 43.5349 11.7912C43.5633 11.7849 43.5909 11.775 43.615 11.7658C43.6773 11.7444 43.7369 11.7273 43.7992 11.7059C43.8865 11.6789 44.0096 11.6289 44.1587 11.562C44.1875 11.5742 44.2207 11.5891 44.2496 11.6013C45.0081 12.0733 45.6704 12.4325 45.8066 12.4244C46.1233 12.4058 49.2109 12.0016 50.4487 11.549C50.4594 11.5466 50.4736 11.5435 50.4827 11.534C50.5099 11.5391 50.5372 11.5441 50.5652 11.5528C50.884 11.6269 52.4934 11.9345 52.9298 11.5009C52.994 11.438 53.0325 11.3436 53.0091 11.2555C52.9812 11.1311 52.8548 11.0662 52.7386 11.0138C52.4478 10.8811 51.6836 10.5336 51.5872 10.5029C51.5022 10.4734 51.4145 10.4483 51.3261 10.4531C51.2227 10.4577 51.1272 10.4977 51.0361 10.5405C50.8735 10.6142 50.7098 10.6994 50.5314 10.7356C50.4391 10.7563 50.3258 10.8003 49.953 10.7345C49.8941 10.7216 49.8325 10.713 49.7725 10.7115C49.746 10.71 49.7002 10.7053 49.6386 10.6967C49.8761 10.5577 50.0792 10.4152 50.2345 10.2759C50.2408 10.2708 50.2499 10.2613 50.2562 10.2561C50.2862 10.2569 50.3197 10.2568 50.3497 10.2576C50.6771 10.2701 52.3133 10.2806 52.6622 9.77327C52.7126 9.69854 52.7318 9.60095 52.6985 9.51884C52.6466 9.40358 52.5099 9.35956 52.3878 9.33091C52.0797 9.25437 51.2625 9.05301 51.1622 9.03816C51.0733 9.02447 50.9817 9.01513 50.8972 9.03778C50.7978 9.06005 50.7098 9.11707 50.6262 9.17684C50.4779 9.28095 50.3356 9.39493 50.1679 9.46234C50.0787 9.49724 49.9782 9.56454 49.5984 9.5675C49.5383 9.56602 49.4756 9.56888 49.4179 9.57805C49.2435 9.59847 48.3586 9.59511 47.6329 9.58596C47.823 9.43518 48.0174 9.28715 48.2244 9.16241C48.5723 8.95015 52.8322 6.95121 52.9671 6.85383C53.0633 6.78377 53.1246 6.62449 53.0943 6.45588C53.0943 6.45588 53.0895 6.43456 53.0879 6.42745C53.0864 6.42034 53.0848 6.41324 53.0832 6.40613C53.0824 6.40258 53.0816 6.39902 53.0808 6.39547C53.4277 6.32897 53.6616 6.19075 53.8143 5.90652C53.8615 5.81757 53.9217 5.66975 53.9837 5.48046C54.0492 5.43967 54.2402 5.14313 54.1718 4.83752C54.1598 4.78421 54.14 4.72895 54.1087 4.67251C54.0952 4.64567 54.0644 4.6078 54.0299 4.57073L54.0287 4.54861Z"
              fill="#ECECEC"
            /> 
          </svg>

          <h1 className="auth-login"> Log In</h1>
        </div>
        <InvisibleRecaptcha inputRef={reCaptchaRef} />
        <p className="auth__message-otp">
          <strong>Log in</strong> easily with a one-time code.
        </p>
        <TabSwitch onChange={tabSwitchHandler} activeTab={activeTab} />
        {returnFormFields()}
        {errorMessage && (
          <div className="form-group">
            <span role="alert" className="sl-auth__error">
              <ExclamationIcon /> {errorMessage}
            </span>
          </div>
        )}

        <div className="form-group form-group__action">
          {/* Removed login button, might be used later */}
          {/* {activeTab === 'Email' && (
              <button
                className="button button__primary sl-auth__register-button"
                disabled={
                  googleProcessing ||
                  isProcessing ||
                  (email.length > 0 && password.length > 0 ? false : true)
                }
              >
                Log In
              </button>
            )} */}
          <button
            type="submit"
            className={`button sl-auth__register-button ${
              isOTPDisabled() ? 'button__secondary' : 'button__primary'
            } button-otp`}
            disabled={isOTPDisabled()}
            onClick={updateVerifyOTP}
          >
            {Account_Message.codeLabel}
          </button>
        </div>

        <div className="sl-auth__or">
          <span className="sl-auth__or-label">Or</span>
        </div>
        <GoogleButton
          processing={googleProcessing}
          label={Account_Message.googleLoginLabel}
          setProcessing={setGoogleProcessing}
          registerRedirect={registerRedirect}
          handleRedirection={handleRedirection}
          setMainLoading={setLoading}
        />

        <FacebookButton
          text={Account_Message.facebookLoginLabel}
          registerRedirect={registerRedirect}
          handleRedirection={handleRedirection}
          setMainLoading={setLoading}
        />

        <AppleButton
          text={Account_Message.appleLoginLabel}
          registerRedirect={registerRedirect}
          handleRedirection={handleRedirection}
          setMainLoading={setLoading}
        />
      </form>
    );
  };

  return (
    <AuthHeaderLayout title="Log In" helmetTitle="Scrappi">
      {showConfirmation ? (
        <ConfirmationScreen
          onConfirmClick={() => {
            setShowConfirmation(false);
            try {
              registerGoogle(socialFormData);
            } catch (e) {
              handleError(e);
            }
          }}
          errorMessage={Account_Message.accountNotFound}
          errorDescription={Account_Message.createAccountDescription}
          backButtonLabel={Account_Message.backToLogin}
          confirmButtonLabel={Account_Message.createAccount}
        />
      ) : (
        <>
          {(googleProcessing || isProcessing || loading) && (
            <div className="auth__loading">
              <FontAwesomeIcon icon={faSpinnerIcon} spin size="lg" />
            </div>
          )}
          {returnWrapper()}
        </>
      )}
    </AuthHeaderLayout>
  );
};

export default Login;
